import type { ApiErrorBody, ApiErrorCode, ApiRoute, ApiRoutes } from '@casino/shared';
import { adoptResponseUser, clearSession, getSessionToken } from '../state/store.js';

/**
 * Client HTTP type sur `ApiRoutes` (packages/shared/src/types/api.ts).
 *
 * `/api/**` est servi SAME-ORIGIN (Caddy sur le VPS en production, proxy de
 * `vite dev` en local) : on ne le prefixe surtout pas par `/.proxy/`, sinon
 * Discord le remappe vers un hote externe et la requete part dans le vide.
 *
 * Toutes les routes sauf `/api/token` exigent
 * `Authorization: Bearer <sessionToken>`, le JWT maison emis par le serveur.
 * L'uid est lu du token cote serveur, jamais envoye dans le body.
 *
 * Toute reponse portant un `user` est adoptee par l'etat (`adoptResponseUser`) :
 * le solde affiche vient donc TOUJOURS du serveur, jamais d'un calcul local.
 */

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;

  constructor(code: ApiErrorCode, message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }

  /** Message court, en francais, montrable tel quel dans un toast. */
  get userMessage(): string {
    switch (this.code) {
      case 'unauthenticated':
        return 'Session expiree. Relance l\'activite depuis Discord.';
      case 'permission-denied':
        return 'Action refusee.';
      case 'invalid-argument':
        return this.message;
      case 'not-found':
        return 'Introuvable.';
      case 'failed-precondition':
        return this.message;
      case 'resource-exhausted':
        return 'Trop vite. Laisse passer un instant.';
      case 'internal':
        return 'Le serveur a rencontre une erreur.';
      default:
        return this.message;
    }
  }
}

const API_ERROR_CODES: ReadonlySet<string> = new Set<ApiErrorCode>([
  'unauthenticated',
  'permission-denied',
  'invalid-argument',
  'not-found',
  'failed-precondition',
  'resource-exhausted',
  'internal',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Narrowing runtime du format d'erreur partage `{ error: { code, message } }`. */
function parseApiError(payload: unknown): ApiErrorBody | null {
  if (!isRecord(payload)) return null;
  const error = payload['error'];
  if (!isRecord(error)) return null;
  const code = error['code'];
  const message = error['message'];
  if (typeof code !== 'string' || !API_ERROR_CODES.has(code)) return null;
  if (typeof message !== 'string') return null;
  return { error: { code: code as ApiErrorCode, message } };
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function authHeader(): Record<string, string> {
  const token = getSessionToken();
  if (token === null) {
    throw new ApiError('unauthenticated', 'Aucune session active.', 0);
  }
  return { Authorization: `Bearer ${token}` };
}

// ---------------------------------------------------------------------------
// Re-authentification
// ---------------------------------------------------------------------------

/**
 * Refait `authorize` -> `/api/token` et repose un jeton frais dans l'etat.
 * Injecte par `discord/auth.ts` : le client HTTP n'a pas a connaitre le SDK
 * Discord, et on evite le cycle d'imports api <-> auth.
 */
export type Reauthenticator = () => Promise<void>;

let reauthenticator: Reauthenticator | null = null;
let pendingReauth: Promise<boolean> | null = null;

export function setReauthenticator(handler: Reauthenticator | null): void {
  reauthenticator = handler;
}

/**
 * Une seule re-authentification a la fois : si trois appels se prennent un 401
 * en meme temps, ils attendent tous la meme promesse au lieu d'ouvrir trois
 * fenetres d'autorisation.
 */
async function refreshSession(): Promise<boolean> {
  const handler = reauthenticator;
  if (handler === null) return false;

  pendingReauth ??= handler()
    .then(() => getSessionToken() !== null)
    .catch(() => false)
    .finally(() => {
      pendingReauth = null;
    });

  return pendingReauth;
}

export interface ApiCallOptions {
  /** `false` uniquement pour `/api/token`, la seule route publique. */
  readonly authenticated?: boolean;
  readonly signal?: AbortSignal;
  /**
   * `false` pour DIFFERER l'adoption de l'etat porte par la reponse.
   *
   * Les jeux s'en servent : le serveur a deja tranche, mais afficher le nouveau
   * solde avant l'arret des rouleaux reviendrait a divulguer le resultat par le
   * HUD. L'appelant DOIT alors appeler `applyUserState(response.user)` lui-meme
   * une fois l'animation finie.
   *
   * La regle d'or est intacte : le solde vient toujours du serveur, seul
   * l'INSTANT de son affichage change. Rien n'est calcule localement.
   */
  readonly adoptUser?: boolean;
}

async function request<TRes>(
  route: string,
  body: unknown,
  authenticated: boolean,
  signal: AbortSignal | undefined,
  adoptUser: boolean,
): Promise<TRes> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authenticated) Object.assign(headers, authHeader());

  let response: Response;
  try {
    response = await fetch(route, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    throw new ApiError(
      'internal',
      cause instanceof Error ? `Reseau indisponible : ${cause.message}` : 'Reseau indisponible.',
      0,
    );
  }

  const payload = await readJson(response);

  if (!response.ok) {
    const parsed = parseApiError(payload);
    if (parsed) throw new ApiError(parsed.error.code, parsed.error.message, response.status);
    throw new ApiError('internal', `Reponse ${response.status} inattendue du serveur.`, response.status);
  }

  if (payload === null) {
    throw new ApiError('internal', 'Reponse vide du serveur.', response.status);
  }

  // Le solde et le profil sont adoptes ici, une fois pour toutes les routes.
  if (adoptUser) adoptResponseUser(payload);

  // Le serveur est la source de verite du contrat : on lui fait confiance ici,
  // les erreurs de forme se manifesteraient a l'affichage, pas en silence.
  return payload as TRes;
}

/**
 * Appelle une route de `ApiRoutes` : le corps et la reponse sont deduits du
 * chemin, aucune assertion de type au point d'appel.
 *
 * Sur `unauthenticated`, on tente UNE re-authentification puis on rejoue la
 * requete. Le second appel passe par `request` directement : la branche de
 * secours n'est jamais reentrante, il ne peut donc pas y avoir de boucle.
 */
export async function apiCall<R extends ApiRoute>(
  route: R,
  body: ApiRoutes[R]['req'],
  options: ApiCallOptions = {},
): Promise<ApiRoutes[R]['res']> {
  return apiPost<ApiRoutes[R]['res']>(route, body, options);
}

/**
 * Meme chemin que `apiCall`, mais pour les routes qui ne figurent pas (encore)
 * dans `ApiRoutes` : les tables multijoueur (`/api/table/**`) sont decrites par
 * `table-api.ts` sans etre indexees dans la table des routes du paquet partage.
 *
 * Le typage reste explicite au point d'appel (`api/tables.ts`), et surtout le
 * comportement est identique : Bearer, adoption de l'etat serveur, et UNE
 * re-authentification sur `unauthenticated` avant de rejouer la requete.
 */
export async function apiPost<TRes>(
  route: string,
  body: unknown,
  options: ApiCallOptions = {},
): Promise<TRes> {
  const authenticated = options.authenticated ?? true;
  const signal = options.signal;
  const adoptUser = options.adoptUser ?? true;

  try {
    return await request<TRes>(route, body, authenticated, signal, adoptUser);
  } catch (error) {
    const recoverable =
      authenticated && error instanceof ApiError && error.code === 'unauthenticated';
    if (!recoverable) throw error;

    const renewed = await refreshSession();
    if (!renewed) {
      // Le serveur a rejete ce jeton et on n'a pas su en obtenir un autre : on
      // le jette (il ne vaut plus rien) et on remonte l'erreur d'origine a
      // l'UI, qui sait l'afficher (« Session expiree. Relance l'activite... »).
      clearSession();
      throw error;
    }

    return await request<TRes>(route, body, authenticated, signal, adoptUser);
  }
}
